import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, SlidersHorizontal, Plus, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { clearRiskBandCache } from '../../utils/riskBands';
import { clearScaleLabelCache } from '../../utils/scaleLabels';

/**
 * Admin modal for tenant HIRA risk settings (EHSMS-F-12) — two tabs:
 *
 * Risk Bands: map the 5×5 risk score (1–25) to a label, color and an
 * Acceptable/Unacceptable verdict; the verdict drives approval gating
 * (server-enforced).
 * Scale Labels: the severity/probability wording for values 1–5 (display
 * only — stored integers stay canonical).
 *
 * Follows the PTWChecklistSettings pattern: server supplies `can_manage`
 * (UI fails closed to read-only), edits are staged locally until Save,
 * reset-to-default is a staged preview.
 *
 * Backend (routes/hira.py):
 *   GET/PUT/DELETE /api/hira/risk-band-config    -> {bands, is_customized, default_bands, can_manage}
 *   GET/PUT/DELETE /api/hira/scale-label-config  -> {severity, probability, is_customized, default_labels, can_manage}
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Bands are kept contiguous by construction: the editor exposes each band's
// upper bound only; mins are derived (first starts at 1, last ends at 25).
const withDerivedMins = (bands) => {
  let nextMin = 1;
  return bands.map((b, i) => {
    const band = { ...b, min: nextMin, max: i === bands.length - 1 ? 25 : b.max };
    nextMin = band.max + 1;
    return band;
  });
};

const HIRARiskBandSettings = ({ open, onClose }) => {
  const { showSuccess, showError, confirm } = useAlert();
  const [activeTab, setActiveTab] = useState('bands'); // 'bands' | 'labels'
  const [bands, setBands] = useState([]);
  const [defaultBands, setDefaultBands] = useState([]);
  const [customized, setCustomized] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Reset is only STAGED in the editor until the user clicks Save.
  const [pendingReset, setPendingReset] = useState(false);

  // Scale labels tab state
  const [labels, setLabels] = useState({ severity: [], probability: [] });
  const [defaultLabels, setDefaultLabels] = useState(null);
  const [labelsCustomized, setLabelsCustomized] = useState(false);
  const [pendingLabelReset, setPendingLabelReset] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setPendingReset(false);
    setPendingLabelReset(false);
    try {
      const [bandRes, labelRes] = await Promise.all([
        axios.get(`${API}/hira/risk-band-config`, authHeaders()),
        axios.get(`${API}/hira/scale-label-config`, authHeaders()),
      ]);
      setBands(withDerivedMins(bandRes.data?.bands || []));
      setDefaultBands(bandRes.data?.default_bands || []);
      setCustomized(!!bandRes.data?.is_customized);
      setCanManage(!!bandRes.data?.can_manage);
      setLabels({
        severity: labelRes.data?.severity || [],
        probability: labelRes.data?.probability || [],
      });
      setDefaultLabels(labelRes.data?.default_labels || null);
      setLabelsCustomized(!!labelRes.data?.is_customized);
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to load risk configuration.');
      setBands([]);
      setCanManage(false);
    } finally {
      setLoading(false);
    }
  }, []); // deliberately dependency-free — see PTWChecklistSettings loadTemplate

  useEffect(() => {
    if (open) loadConfig();
  }, [open, loadConfig]);

  const updateLabel = (axis, i, field, value) => {
    setPendingLabelReset(false);
    setLabels(prev => ({
      ...prev,
      [axis]: prev[axis].map((e, idx) => (idx === i ? { ...e, [field]: value } : e)),
    }));
  };

  const validateLabels = () => {
    for (const axis of ['severity', 'probability']) {
      const entries = labels[axis] || [];
      if (entries.length !== 5) return `Exactly 5 ${axis} entries are required.`;
      const names = entries.map(e => (e.label || '').trim().toLowerCase());
      if (names.some(n => !n)) return `Every ${axis} entry needs a label.`;
      if (new Set(names).size !== names.length) return `${axis} labels must be unique.`;
    }
    return null;
  };

  const updateBand = (i, field, value) => {
    setPendingReset(false);
    setBands(prev => withDerivedMins(prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b))));
  };

  const addBand = () => {
    if (bands.length >= 6) { showError('A maximum of 6 bands is allowed.'); return; }
    setPendingReset(false);
    // Split the last band: new band takes the upper part.
    setBands(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      const splitAt = Math.max(last.min, 25 - 4);
      next[next.length - 1] = { ...last, max: splitAt - 1 };
      next.push({ min: splitAt, max: 25, label: 'New Band', color: '#EF4444', acceptable: false });
      return withDerivedMins(next);
    });
  };

  const removeBand = (i) => {
    if (bands.length <= 2) { showError('At least 2 bands are required.'); return; }
    setPendingReset(false);
    setBands(prev => withDerivedMins(prev.filter((_, idx) => idx !== i)));
  };

  const validate = () => {
    const list = withDerivedMins(bands);
    if (list.length < 2 || list.length > 6) return 'Between 2 and 6 bands are required.';
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      if (!String(b.label || '').trim()) return `Band ${i + 1} needs a label.`;
      if (!(b.min >= 1 && b.max <= 25 && b.min <= b.max)) {
        return `Band '${b.label}' has an invalid score range (${b.min}–${b.max}).`;
      }
    }
    const labels = list.map(b => b.label.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) return 'Band labels must be unique.';
    if (!list.some(b => !b.acceptable)) {
      return 'At least one band must be Unacceptable — otherwise approval gating is disabled.';
    }
    return null;
  };

  const save = async () => {
    if (!canManage) { showError('You do not have permission to edit risk configuration.'); return; }
    setSaving(true);
    try {
      if (activeTab === 'bands') {
        if (pendingReset) {
          await axios.delete(`${API}/hira/risk-band-config`, authHeaders());
          showSuccess('Risk bands reset to the product default.');
          clearRiskBandCache();
          await loadConfig();
        } else {
          const err = validate();
          if (err) { showError(err); setSaving(false); return; }
          const cleaned = withDerivedMins(bands).map(b => ({
            min: b.min,
            max: b.max,
            label: b.label.trim(),
            color: b.color,
            acceptable: !!b.acceptable,
          }));
          await axios.put(`${API}/hira/risk-band-config`, { bands: cleaned }, authHeaders());
          setCustomized(true);
          clearRiskBandCache();
          showSuccess('Risk bands saved. Risk levels and approval gating now use the new bands.');
        }
      } else {
        if (pendingLabelReset) {
          await axios.delete(`${API}/hira/scale-label-config`, authHeaders());
          showSuccess('Scale labels reset to the product default.');
          clearScaleLabelCache();
          await loadConfig();
        } else {
          const err = validateLabels();
          if (err) { showError(err); setSaving(false); return; }
          const cleaned = {
            severity: labels.severity.map(e => ({ value: e.value, label: e.label.trim(), description: (e.description || '').trim() })),
            probability: labels.probability.map(e => ({ value: e.value, label: e.label.trim(), description: (e.description || '').trim() })),
          };
          await axios.put(`${API}/hira/scale-label-config`, cleaned, authHeaders());
          setLabelsCustomized(true);
          clearScaleLabelCache();
          showSuccess('Scale labels saved. Severity/probability dropdowns and the Excel export now use the new wording.');
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to save risk configuration.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!canManage) { showError('You do not have permission to edit risk configuration.'); return; }
    if (activeTab === 'bands') {
      const ok = await confirm(
        'Reset risk bands to default?',
        'The product default bands will be loaded into the editor. The reset is applied only when you click Save — closing without saving keeps your customized bands.',
        { confirmLabel: 'Load default', cancelLabel: 'Cancel', destructive: true }
      );
      if (!ok) return;
      setBands(withDerivedMins(defaultBands));
      setPendingReset(true);
    } else {
      const ok = await confirm(
        'Reset scale labels to default?',
        'The product default severity/probability wording will be loaded into the editor. The reset is applied only when you click Save — closing without saving keeps your customized labels.',
        { confirmLabel: 'Load default', cancelLabel: 'Cancel', destructive: true }
      );
      if (!ok || !defaultLabels) return;
      setLabels({
        severity: defaultLabels.severity.map(e => ({ ...e })),
        probability: defaultLabels.probability.map(e => ({ ...e })),
      });
      setPendingLabelReset(true);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">HIRA Risk Settings</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {[['bands', 'Risk Bands'], ['labels', 'Scale Labels']].map(([key, title]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {title}
            </button>
          ))}
        </div>

        {activeTab === 'labels' ? (
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Wording for the 1–5 severity and probability scales, shown in the hazard
            form dropdowns and the Excel export criteria sheet. Display only — stored
            scores are unaffected.
          </p>
          <p className="text-xs text-gray-500">
            {labelsCustomized ? 'Customized for this tenant.' : 'Using the product default (EHSMS-F-12 wording).'}
          </p>

          {pendingLabelReset && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
              Default labels loaded as a preview. Click <strong>Save</strong> to apply the reset —
              closing without saving keeps your customized labels.
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-5">
              {[['severity', 'Severity (Injury / Ill Health)'], ['probability', 'Probability (Likelihood)']].map(([axis, title]) => (
                <div key={axis}>
                  <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
                  <div className="space-y-2">
                    {(labels[axis] || []).map((entry, i) => (
                      <div key={entry.value} className="grid grid-cols-12 gap-2 items-start">
                        <span className="col-span-1 text-sm font-bold text-gray-600 pt-2 text-center">{entry.value}</span>
                        <div className="col-span-4">
                          {canManage ? (
                            <input
                              type="text"
                              value={entry.label}
                              maxLength={50}
                              onChange={(e) => updateLabel(axis, i, 'label', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                              placeholder="Label"
                            />
                          ) : (
                            <span className="text-sm text-gray-800">{entry.label}</span>
                          )}
                        </div>
                        <div className="col-span-7">
                          {canManage ? (
                            <textarea
                              value={entry.description || ''}
                              maxLength={500}
                              rows={2}
                              onChange={(e) => updateLabel(axis, i, 'description', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                              placeholder="Explanation (optional)"
                            />
                          ) : (
                            <span className="text-xs text-gray-600 whitespace-pre-wrap">{entry.description}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        ) : (
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Bands map the 5×5 risk score (1–25) to a risk level. Assessments cannot be
            approved while any hazard's residual risk falls in an <strong>Unacceptable</strong> band.
          </p>
          <p className="text-xs text-gray-500">
            {customized ? 'Customized for this tenant.' : 'Using the product default (not yet customized).'}
          </p>

          {pendingReset && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
              Default bands loaded as a preview. Click <strong>Save</strong> to apply the reset —
              closing without saving keeps your customized bands.
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-1">
                <span className="col-span-2">Scores</span>
                <span className="col-span-4">Label</span>
                <span className="col-span-2">Color</span>
                <span className="col-span-3">Verdict</span>
                <span className="col-span-1" />
              </div>
              {bands.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-2 flex items-center gap-1 text-sm text-gray-700">
                    <span className="w-6 text-right">{b.min}</span>
                    <span className="text-gray-400">–</span>
                    {canManage && i < bands.length - 1 ? (
                      <input
                        type="number"
                        min={b.min}
                        max={24}
                        value={b.max}
                        onChange={(e) => updateBand(i, 'max', Math.max(b.min, Math.min(24, parseInt(e.target.value) || b.min)))}
                        className="w-14 px-1 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="w-6">{b.max}</span>
                    )}
                  </div>
                  <div className="col-span-4">
                    {canManage ? (
                      <input
                        type="text"
                        value={b.label}
                        maxLength={30}
                        onChange={(e) => updateBand(i, 'label', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-800">{b.label}</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    {canManage ? (
                      <input
                        type="color"
                        value={b.color}
                        onChange={(e) => updateBand(i, 'color', e.target.value.toUpperCase())}
                        className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                        title={b.color}
                      />
                    ) : (
                      <span className="inline-block w-6 h-6 rounded" style={{ backgroundColor: b.color }} />
                    )}
                  </div>
                  <div className="col-span-3">
                    {canManage ? (
                      <select
                        value={b.acceptable ? 'acceptable' : 'unacceptable'}
                        onChange={(e) => updateBand(i, 'acceptable', e.target.value === 'acceptable')}
                        className={`w-full px-2 py-1 border rounded text-sm font-semibold ${
                          b.acceptable ? 'text-green-700 border-green-300' : 'text-red-700 border-red-300'
                        }`}
                      >
                        <option value="acceptable">Acceptable</option>
                        <option value="unacceptable">Unacceptable</option>
                      </select>
                    ) : (
                      <span className={`text-sm font-semibold ${b.acceptable ? 'text-green-700' : 'text-red-700'}`}>
                        {b.acceptable ? 'Acceptable' : 'Unacceptable'}
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => removeBand(i)}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30"
                        disabled={bands.length <= 2}
                        title="Remove band"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {canManage && bands.length < 6 && (
                <Button type="button" size="sm" variant="outline" onClick={addBand}>
                  <Plus className="w-3 h-3 mr-1" />Add band
                </Button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div>
            {canManage && (
              <Button
                type="button"
                variant="outline"
                onClick={resetToDefault}
                disabled={saving || loading || (activeTab === 'bands' ? !customized : !labelsCustomized)}
                title="Delete this tenant's override and use the product default"
              >
                <RotateCcw className="w-4 h-4 mr-1" />Reset to default
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
            {canManage && (
              <Button type="button" onClick={save} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HIRARiskBandSettings;
